/**
 * AI FAB — 滾動才出現（scroll-reveal）
 *
 * click handler（開啟對話框）在 chat-dialog.js，本檔僅負責「何時可見」。
 *
 * 行為：fab 初始隱藏（CSS：opacity:0 + translateY + pointer-events:none + visibility:hidden），
 * 捲過首屏後淡入（加 .is-visible），捲回頂部再淡出（移除 .is-visible）。雙向。
 *
 * 三種頁面、三種「該不該顯示」判準（共用同一組 show/hide/sync）：
 *   1. 首頁（有 .hero）→ IntersectionObserver 觀察 .hero：
 *        hero 完全離開視窗 → 顯示；hero 任一 px 進入視窗 → 隱藏。
 *        用 IO 而非 scroll listener，效能最佳（hero 是穩定錨點）。
 *   2. 前台內頁（無 .hero，如 articles / clinics / products / about / 各詳情頁）→ scroll 閾值：
 *        scrollY > REVEAL_THRESHOLD(300) 顯示；回到近頂（≤ 閾值）隱藏。
 *        內頁沒有等高首屏可觀察，故退回 scrollY 判準。rAF 節流，passive listener。
 *   3. 後台 / 會員端（body.page-admin / .page-member）→ 進頁即恆顯：
 *        後台無 hero，且 dashboard / 短表單頁常捲不到 300px（套閾值會永不出現），
 *        故 shouldShow 恆真、不綁捲動，對齊首頁「右下角出現」的最終結果。
 *
 *   三路只差「shouldShow 怎麼算」；顯隱機制、chat-open lock、無障礙語意完全共用。
 *
 * 邊緣處理 — chat 對話框開啟時：
 *   fab 是「開啟 chat」的觸發鈕，使用者必須先看到 fab（已捲過首屏）才能點開 chat，
 *   故 chat 不會在 fab 隱藏時開啟。唯一尷尬情境是 chat 開著時又捲回頂部 —— 此時若隱藏
 *   fab，關閉 chat 後焦點要還給一個隱藏元素（chat-dialog.js closeDialog 會 fab.focus()），
 *   破壞無障礙焦點。故 chat 開啟期間「鎖定」fab 可見，關閉後再依當前捲動狀態重新同步。
 *   以 MutationObserver 監看 dialog 的 class（只在開/關時觸發，零輪詢），不動 chat-dialog.js。
 *
 * reduced-motion：CSS 已將過渡退化為瞬顯/無位移；本檔仍照常 toggle .is-visible，
 *   只是顯隱變即時，scroll-reveal 行為與無障礙語意不變。
 */

const REVEAL_THRESHOLD = 300; // 內頁捲動超過此 px 才顯示 fab

const fab = document.querySelector('.ai-fab');

if (fab) {
  const hero = document.querySelector('.hero');
  const dialog = document.getElementById('chat-dialog');

  const isChatOpen = () => !!dialog && dialog.classList.contains('is-open');

  const showFab = () => {
    fab.classList.add('is-visible');
    fab.removeAttribute('aria-hidden');
  };

  const hideFab = () => {
    // chat 開啟期間鎖定可見，避免關閉後焦點落在隱藏元素上
    if (isChatOpen()) return;
    fab.classList.remove('is-visible');
    fab.setAttribute('aria-hidden', 'true');
  };

  // 「目前是否該顯示 fab」的真相來源（首頁/內頁各自覆寫）。
  let shouldShow = () => false;

  // 依 shouldShow() 同步顯隱（observer / scroll / chat 關閉皆呼叫此單一入口）
  const syncFab = () => {
    if (shouldShow()) {
      showFab();
    } else {
      hideFab();
    }
  };

  // chat 關閉時，依當前捲動狀態重新同步（解開「鎖定可見」）
  if (dialog) {
    let wasOpen = isChatOpen();
    const dialogStateObserver = new MutationObserver(() => {
      const openNow = isChatOpen();
      if (wasOpen && !openNow) {
        syncFab(); // 剛關閉 → 若已捲回首屏則隱藏
      }
      wasOpen = openNow;
    });
    dialogStateObserver.observe(dialog, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  // 後台 / 會員端外殼（page-admin / page-member）：無條件顯示 fab。
  // 前台內頁用 scrollY>300 閾值是為了「捲過首屏才出現、避免蓋住 hero / 頁首主視覺」，
  // 但後台頁無 .hero，且 dashboard / 短表單頁的內容高度常 < 首屏（永遠捲不到 300px），
  // 套用閾值會導致 fab 永不出現。後台改為進頁即顯示（對齊首頁「右下角出現」的最終結果），
  // 顯隱機制 / chat-open lock / 無障礙語意與前台完全共用，只是 shouldShow 恆真、不綁捲動。
  const isAdminShell =
    document.body.classList.contains('page-admin') ||
    document.body.classList.contains('page-member');

  if (isAdminShell) {
    // ===== 後台 / 會員端：恆顯（不綁捲動） =====
    shouldShow = () => true;
    syncFab();
  } else if (hero && 'IntersectionObserver' in window) {
    // ===== 首頁：IntersectionObserver 觀察 .hero =====
    let heroIntersecting = true;
    shouldShow = () => !heroIntersecting;

    const heroObserver = new IntersectionObserver((entries) => {
      heroIntersecting = entries[0].isIntersecting;
      syncFab();
    }, {
      threshold: 0,
      rootMargin: '0px',
    });

    heroObserver.observe(hero);
  } else {
    // ===== 內頁（無 .hero）：scrollY 閾值 =====
    shouldShow = () => window.scrollY > REVEAL_THRESHOLD;

    // rAF 節流：scroll 事件高頻，合併到單一 frame 內計算一次
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        syncFab();
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // 初始判定：處理 deep-link / bfcache 還原 / 重整時已非頂部的情況
    syncFab();
  }
}
